import {
  NewOrderImpressionsMode,
  TBaseKeyword,
  TNewOrderCountry,
  TViewsKeyValue,
  TViewsKeyword,
} from "@/entities/new-order";
import {
  getDatesListFromCountry,
  getKeysFromCountry,
} from "@/entities/new-order/utils";
import {
  calcImpressions,
  getFirstCountryKeywordValue,
} from "@/features/new-order/country-item/lib";
import { getOneDayAgo } from "@/Utils/DateUtils";
import { throwNewToastError } from "@/Utils/MainUtils";
import { useCallback, useState } from "react";

interface UseRatioHandlerParams {
  country: TNewOrderCountry;
  updateCountry: (newCountry: TNewOrderCountry) => void;
  countryIndex: number;
}

type TEditableKeywords = TBaseKeyword | TViewsKeyword;
type TEditableKeywordValue = number | TViewsKeyValue[string];
type TEditableCountry = Omit<TNewOrderCountry, "keywords"> & {
  keywords: TEditableKeywords;
};

export const useRatioHandler = ({
  country,
  updateCountry,
}: UseRatioHandlerParams) => {
  const [ratioKey, setRatioKey] = useState<string | null>(null);
  const [ratioModalOpen, setRatioModalOpen] = useState(false);

  // === Вспомогательные функции ===
  const isViewsKeyword = (
    keywords: TEditableKeywords,
  ): keywords is TViewsKeyword => {
    return !!country.impressions;
  };

  const getInstallValue = (keywordValue: TEditableKeywordValue): number => {
    if (typeof keywordValue === "number") {
      return keywordValue;
    }
    return keywordValue.installs;
  };

  const createEmptyKeywordValue = (): TEditableKeywordValue => {
    if (isViewsKeyword(country.keywords as TEditableKeywords)) {
      return { installs: 0, views: 0, CR: 100 };
    }
    return 0;
  };

  const createKeywordValue = (
    installs: number,
    existingValue?: TEditableKeywordValue,
  ): TEditableKeywordValue => {
    if (isViewsKeyword(country.keywords as TEditableKeywords)) {
      const existing = existingValue as TViewsKeyValue[string];
      const CR = existing?.CR || 0;
      const isCRMode = country.impressions?.type === NewOrderImpressionsMode.CR;

      // Пересчитываем views только в режиме CR и если CR задан
      const views =
        isCRMode && CR ? calcImpressions(installs, CR) : existing?.views || 0;

      return {
        installs,
        views,
        CR,
      };
    }
    return installs;
  };

  // === Управление модалкой ===
  const openRatioModal = useCallback((key: string | null) => {
    setRatioKey(key);
    setRatioModalOpen(true);
  }, []);

  const closeRatioModal = useCallback(() => {
    setRatioModalOpen(false);
    setRatioKey(null);
  }, []);

  // === Основная логика ===
  const setRatio = useCallback(
    (
      type: string,
      valueRatio: number | null = null,
      key: string | null = null,
    ) => {
      const newCountry = structuredClone(country) as TEditableCountry;
      const newKeywords: TBaseKeyword = {};
      let shouldBreak = false;

      switch (type) {
        case "deleteRow":
          if (key) {
            if (getKeysFromCountry(newCountry).length > 1) {
              getDatesListFromCountry(newCountry).forEach((date) => {
                delete newCountry.keywords[date][key];
              });
            } else {
              newCountry.keywords = {};
            }
          }
          break;

        case "clearRow":
          if (key) {
            getDatesListFromCountry(newCountry).forEach((date) => {
              newCountry.keywords[date][key] = createEmptyKeywordValue();
            });
          }
          break;

        case "clearAll":
          for (const date of Object.keys(newCountry.keywords)) {
            for (const k of Object.keys(newCountry.keywords[date])) {
              newCountry.keywords[date][k] = createEmptyKeywordValue();
            }
          }
          break;

        case "autofill":
          if (valueRatio) {
            for (const date of Object.keys(newCountry.keywords)) {
              for (const k of Object.keys(newCountry.keywords[date])) {
                const existingValue = newCountry.keywords[date][k];
                newCountry.keywords[date][k] = createKeywordValue(
                  valueRatio,
                  existingValue,
                );
              }
            }
            closeRatioModal();
          } else {
            throwNewToastError("Please enter a value");
          }
          break;

        case "percent":
        case "amount":
        case "deviation":
          if (valueRatio === null) {
            throwNewToastError("Please enter a value");
            break;
          }

          if (valueRatio === 0 && type !== "percent") {
            throwNewToastError("Please enter a value");
            break;
          }

          for (const date of Object.keys(newCountry.keywords)) {
            const dateAgo = getOneDayAgo(date);
            newKeywords[date] = {};

            for (const k of Object.keys(newCountry.keywords[date])) {
              if (k === key || key === null) {
                if (newCountry.keywords[dateAgo] !== undefined) {
                  const prevValue = newCountry.keywords[dateAgo][k];
                  const prevInstalls = getInstallValue(prevValue);
                  const currentValue = newCountry.keywords[date][k];

                  if (type === "percent") {
                    const newInstalls = Math.max(
                      Number(
                        (
                          prevInstalls *
                          ((100 + Number(valueRatio)) / 100)
                        ).toFixed(0),
                      ),
                      0,
                    );
                    newCountry.keywords[date][k] = createKeywordValue(
                      newInstalls,
                      currentValue,
                    );
                  } else if (type === "amount") {
                    const newInstalls = Math.max(
                      prevInstalls + Number(valueRatio),
                      0,
                    );
                    newCountry.keywords[date][k] = createKeywordValue(
                      newInstalls,
                      currentValue,
                    );
                  } else if (type === "deviation") {
                    const firstVal = getFirstCountryKeywordValue(newCountry, k);
                    if (firstVal > Math.abs(Number(valueRatio))) {
                      const newInstalls =
                        firstVal +
                        (Math.floor(Math.random() * (Number(valueRatio) * 2)) -
                          Number(valueRatio));
                      newCountry.keywords[date][k] = createKeywordValue(
                        newInstalls,
                        currentValue,
                      );
                    } else {
                      throwNewToastError(
                        "The deviation value must be less than the minimum key value",
                      );
                      shouldBreak = true;
                      break;
                    }
                  }
                }
              }
            }
            if (shouldBreak) {
              break;
            }
          }
          closeRatioModal();
          break;
      }

      updateCountry(newCountry);
    },
    [country, updateCountry, closeRatioModal],
  );

  return {
    ratioKey,
    ratioModalOpen,
    openRatioModal,
    closeRatioModal,
    setRatio,
  };
};
